import React from "react";
import * as moment from "moment";
import { capitalize } from "lodash";
import startCase from "lodash/startCase";
import { Box, Typography } from "@mui/material";

import { getMedicineMultiplier } from "../Medication/Medication";
import { getFollowupFormat } from "../../Modals/Assessment/AssessmentContent";
import {
  EditableTableView,
  filterEmptyTableData
} from "../../../components/EditableTable/EditableTable";
import AdvancedIntraOralContent, {
  hasContentAdvancedIntraOral
} from "../../Modals/Assessment/AdvancedIntraOralContent";

import PrintRow from "./PrintRow";
import { t, tl } from "../../../components/translate";
import { countableTypes } from "../Medication/medicineType";
import { Client } from "../../../interfaces/ClientInterface";
import { SORT_ORDER } from "../AssessmentForm/OPDComponents";
import { clientFullNameSelector } from "../../../reducers/client";
import { Assessment, Medicine } from "../../../interfaces/AssessmentInterfaces";
import ExtraOralContent from "../../Modals/Assessment/ExtraOralContent";
import EyeAssessmentContent from "../../Modals/Assessment/EyeAssessmentContent";
import InfoField from "../../Lab/LabPrint/LabprintComponents/LabPrintInfofield";
import { showDOBAndGender } from "../../Billing/PrintBill/EightyMmPrint/ClientInfo";
import { getLocalDateInAD, scaleFont } from "../../Billing/PrintBill/BillPrintHelpers";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import IntraoralAssessmentContent from "../../Modals/Assessment/IntraoralAssessmentContent";
import DiagnosisRenderer, { hasDiagnosis } from "../../Modals/Assessment/DiagnosisRenderer";
import { formatAssessmentForPrint } from "../../Modals/Assessment/AssessmentComponents";
import PrescriptionSummaryPrint from "./PrescriptionSummaryPrint";

export enum VITALS_SHORT_NAME {
  "RR" = "RR",
  "BMI" = "BMI",
  "BSA(m2)" = "BSA(m2)",
  "OFC" = "OFC",
  "WFH" = "WFH",
  "SPO2" = "SPO2",
  "Bruit" = "Bruit",
  "pulse" = "pulse",
  "Scalp" = "Scalp",
  "Spine" = "Spine",
  "height" = "height",
  "Oedema" = "Oedema",
  "others" = "others",
  "Pallor" = "Pallor",
  "weight" = "weight",
  "Ascites" = "Ascites",
  "Bodyfat" = "Bodyfat",
  "Icterus" = "Icterus",
  "Clubbing" = "Clubbing",
  "Cyanosis" = "Cyanosis",
  "Jaundice" = "Jaundice",
  "Lymph node" = "Lymph node",
  "saturation" = "saturation",
  "Dysmpophism" = "Dysmpophism",
  "respiration" = "respiration",
  "temperature" = "temperature",
  "glucoseLevel" = "glucoseLevel",
  "bloodPressure" = "BP",
  "headCircumference" = "headCircumference"
}

const getPrintableTimeUnit = (times: string): string => {
  if (times === "day") return t("medication.day");
  if (times === "week") return t("medication.week");
  if (times === "month") return t("medication.month");
  return times || "";
};

const getPrintableMealLabel = (meal: string): string => {
  if (meal === "before") return t("assessment.beforeMeal").toLowerCase();
  if (meal === "after") return t("assessment.afterMeal").toLowerCase();
  return "";
};

const getPrintableDurationUnit = (frequencyType: string, duration: number): string => {
  const normalizedFrequencyType = frequencyType?.toLowerCase();

  switch (normalizedFrequencyType) {
    case "day":
    case "days":
      return duration === 1 ? "day" : "days";
    case "week":
    case "weeks":
      return duration === 1 ? "week" : "weeks";
    case "month":
    case "months":
      return duration === 1 ? "month" : "months";
    default:
      return frequencyType;
  }
};

const getPrintableFormLabel = (form: string, count: number): string => {
  const normalizedForm = form?.toLowerCase() || "";

  if (!normalizedForm) return "";
  if (count === 1 || normalizedForm.endsWith("s")) return normalizedForm;

  return `${normalizedForm}s`;
};

const getPrintableMedicationInstruction = (medicine: Medicine): string => {
  const totalMedicineCount =
    countableTypes.includes(medicine.form) && medicine.frequency !== 0
      ? medicine.frequency *
        getMedicineMultiplier(medicine.times, medicine.duration, medicine.frequencyType)
      : 0;
  const durationText =
    medicine.duration > 0
      ? `${medicine.duration} ${getPrintableDurationUnit(
          medicine.frequencyType,
          medicine.duration
        )}`
      : "";
  const instructionParts: string[] = [];

  if (medicine.frequency !== 0) {
    instructionParts.push(
      `${medicine.frequency} ${t("assessment.times")}${getPrintableTimeUnit(medicine.times)}`
    );
  }

  if (medicine.meal) {
    instructionParts.push(getPrintableMealLabel(medicine.meal));
  }

  if (totalMedicineCount > 0) {
    instructionParts.push(
      `(Total ${totalMedicineCount} ${getPrintableFormLabel(
        medicine.form,
        totalMedicineCount
      )} for ${durationText})`
    );
  } else if (durationText) {
    instructionParts.push(`(${durationText})`);
  }

  return instructionParts.join(" ").trim();
};

// assssment client info
export const AssessmentClientInfo = ({
  client,
  customerNumber,
  hideRegistrationInfo,
  showDobOnPrint
}: {
  client: Client;
  customerNumber: string;
  showDobOnPrint?: boolean;
  hideRegistrationInfo?: boolean;
}): React.JSX.Element => (
  <>
    <Typography
      sx={{
        textAlign: "left",
        fontSize: scaleFont("0.4cm"),
        fontWeight: 600,
        paddingBottom: "8px",
        textTransform: "uppercase"
      }}
    >
      {clientFullNameSelector(client)}
      <span style={{ marginLeft: "5px" }}>{showDOBAndGender(client.gender, client.dob, true)}</span>
    </Typography>
    {customerNumber && (
      <InfoField label={t("billPrint.customerNumber")} data={customerNumber} highlight />
    )}
    {!hideRegistrationInfo && client.registrationNo && (
      <InfoField label={t("bills.registrationNo")} data={client.registrationNo} />
    )}
    {showDobOnPrint && client?.dob && (
      <InfoField label={t("billPrint.customerDob")} data={getLocalDateInAD(client.dob)} />
    )}
    {(client.address || client.city) && (
      <InfoField
        label={t("address")}
        data={
          <>
            {client.address && `${capitalize(client.address)},`}
            {client.city && ` ${capitalize(client.city)}`}
          </>
        }
      />
    )}
    {client.phone && (
      <InfoField
        label={t("billing.phoneNo")}
        data={client.phone?.substring(0, 4) === "+977" ? client.phone.substring(4) : client.phone}
      />
    )}
  </>
);

// assessment print components
export const AssessmentPrintComponents = ({
  componentName,
  assessment,
  assessmentLabels
}: {
  componentName: string;
  assessment: Assessment;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assessmentLabels: Record<string, any>;
}): React.JSX.Element | null => {
  const vitalToPrint = formatAssessmentForPrint(assessment.vitals);
  const questionnaireToPrint = formatAssessmentForPrint(assessment.questionnaire);

  const servicesAndTests = [...(assessment?.tests || []), ...(assessment?.services || [])];
  const tabularComponentData =
    assessment.tabularComponent && filterEmptyTableData(assessment.tabularComponent);
  const productMedication = assessment.productMedication ? assessment.productMedication : [];

  switch (componentName) {
    case SORT_ORDER.RECORD_PRESCRIPTION: {
      const prescriptionText =
        assessment.prescriptionAudioSummary || assessment.prescriptionAudioTranscription;

      return prescriptionText ? <PrescriptionSummaryPrint text={prescriptionText} /> : null;
    }

    case SORT_ORDER.CHIEF_COMPLAINTS:
      return assessment.symptoms?.length > 0 && Array.isArray(assessment.symptoms) ? (
        <PrintRow label={assessmentLabels.chiefComplaints}>
          {assessment.symptoms.map((data) => (
            <p key={data.symptom}>{data.symptom}</p>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.PAST_HISTORY_OF_ALLERGY:
      return assessment.pastHistoryOfAllergy ? (
        <PrintRow label={assessmentLabels.pastHistoryOfAllergy}>
          {assessment.pastHistoryOfAllergy}
        </PrintRow>
      ) : null;

    case SORT_ORDER.HOPI:
      return assessment.hopi?.length > 0 ? (
        <PrintRow label={assessmentLabels.hopi}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.hopi}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.EXAMINATION_OR_STATUS:
      return (
        <>
          {assessment.assessment?.length > 0 && (
            <PrintRow label={assessmentLabels.examination}>
              <span style={{ whiteSpace: "pre-line" }}>{assessment.assessment.trim()}</span>
            </PrintRow>
          )}
          {assessment.assessmentImage && (
            <PrintRow label={tl("assessment.image")}>
              <img
                src={assessment.assessmentImage}
                alt="AssessmentImage"
                className="assessment-image"
              />
            </PrintRow>
          )}
        </>
      );

    case SORT_ORDER.INVESTIGATION:
      return assessment.investigation ? (
        <PrintRow label={assessmentLabels.investigation}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.investigation}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.INTRA_ORAL:
      return assessment.intraOral &&
        (Object.keys(assessment.intraOral.primary).length !== 0 ||
          Object.keys(assessment.intraOral.permanent).length !== 0) ? (
        <PrintRow label={assessmentLabels.intraoral}>
          <IntraoralAssessmentContent intraOralDoc={assessment.intraOral} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.EXTRA_ORAL:
      return Object.keys(assessment.extraOral || {}).some((key) => !!assessment.extraOral[key]) ? (
        <PrintRow label={assessmentLabels.extraOral}>
          <ExtraOralContent assessmentDocument={assessment} mode="print" />
        </PrintRow>
      ) : null;

    case SORT_ORDER.ADVANCED_INTRA_ORAL:
      return assessment.advancedIntraOral &&
        hasContentAdvancedIntraOral(assessment.advancedIntraOral) ? (
        <PrintRow label={assessmentLabels.advancedIntraOral} shouldAvoidPageBreak>
          <AdvancedIntraOralContent assessmentDocument={assessment} mode="print" />
        </PrintRow>
      ) : null;

    case SORT_ORDER.EYE:
      return (assessment?.eye && Object.keys(assessment?.eye).length) > 0 ? (
        <PrintRow label={assessmentLabels.eye} shouldAvoidPageBreak>
          <EyeAssessmentContent eyeExaminationDoc={assessment?.eye} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.QUESTIONNAIRE:
      return questionnaireToPrint.length > 0 ? (
        <PrintRow label={assessmentLabels.questionnaire}>
          <Box width="100%">
            {questionnaireToPrint.map((questionnaire, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={index} display="flex" flexWrap="wrap">
                <Box display="flex" width="33%" gap={1}>
                  <Typography fontWeight={600}>{`${startCase(questionnaire.name1)} :`}</Typography>
                  <Typography>{questionnaire.value1}</Typography>
                </Box>
                {questionnaire.name2 && (
                  <Box display="flex" width="33%" gap={1}>
                    <Typography fontWeight={600}>
                      {`${startCase(questionnaire.name2)} :`}
                    </Typography>
                    <Typography>{questionnaire.value2}</Typography>
                  </Box>
                )}
                {questionnaire.name3 && (
                  <Box display="flex" width="33%" gap={1}>
                    <Typography fontWeight={600}>
                      {`${startCase(questionnaire.name3)} :`}
                    </Typography>
                    <Typography>{questionnaire.value3}</Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </PrintRow>
      ) : null;

    case SORT_ORDER.VITALS:
      return vitalToPrint.length > 0 ? (
        <PrintRow label={assessmentLabels.vitals}>
          {vitalToPrint.map((vital, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} display="flex" flexWrap="wrap">
              <Box display="flex" width="33%" gap={0.5}>
                <Typography fontWeight={600}>
                  {`${startCase(VITALS_SHORT_NAME[vital.name1])} :`}
                </Typography>
                <Typography>{vital.value1}</Typography>
              </Box>
              {vital.name2 && (
                <Box display="flex" width="33%" gap={0.5}>
                  <Typography fontWeight={600}>
                    {`${startCase(VITALS_SHORT_NAME[vital.name2])} :`}
                  </Typography>
                  <Typography>{vital.value2}</Typography>
                </Box>
              )}
              {vital.name3 && (
                <Box display="flex" width="33%" gap={0.5}>
                  <Typography fontWeight={600}>
                    {`${startCase(VITALS_SHORT_NAME[vital.name3])} :`}
                  </Typography>
                  <Typography>{vital.value3}</Typography>
                </Box>
              )}
            </Box>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.PROVISIONAL_DIAGNOSIS:
      return assessment.provisionalDiagnosis?.length > 0 ? (
        <PrintRow label={assessmentLabels.provisionalDiagnosis}>
          {assessment.provisionalDiagnosis}
        </PrintRow>
      ) : null;

    case SORT_ORDER.LAB_TEST:
      return servicesAndTests.length > 0 ? (
        <PrintRow label={assessmentLabels.labTest}>
          {servicesAndTests.map((data, index) => (
            <Typography key={data.id}>{`${index + 1}. ${data.name}`}</Typography>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.OTHER_SERVICES:
      return assessment.otherServices?.length > 0 ? (
        <PrintRow label={assessmentLabels.otherServices}>
          {assessment.otherServices.map((data, index) => (
            <Typography key={data.id}>{`${index + 1}. ${data.name}`}</Typography>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.DENTAL_SERVICES:
      return assessment.dentalServices?.length > 0 ? (
        <PrintRow label={assessmentLabels.dentalServices}>
          {assessment.dentalServices.map(({ id, name, teeth = [] }, index) => (
            <Typography key={id}>
              {`${index + 1}. ${name}`}
              {teeth.length > 0 && `, (Teeth: ${teeth.join(", ")})`}
            </Typography>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.EYE_SERVICES:
      return assessment.eyeServices?.length > 0 ? (
        <PrintRow label={assessmentLabels.eyeServices}>
          {assessment.eyeServices.map(({ id, name }, index) => (
            <Typography key={id}>{`${index + 1}. ${name}`}</Typography>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.XRAY_SERVICES:
      return assessment.xrayServices?.length > 0 ? (
        <PrintRow label={assessmentLabels.xrayServices}>
          {assessment.xrayServices.map(({ id, name, teeth = [] }, index) => (
            <Typography key={id}>
              {`${index + 1}. ${name}`}
              {teeth.length > 0 && `, (Teeth: ${teeth.join(", ")})`}
            </Typography>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.TREATMENT_DONE:
      return assessment.treatmentDone?.length ? (
        <PrintRow label={assessmentLabels.treatmentDone}>
          {assessment.treatmentDone
            .filter((item) => item.checked)
            .map((data, index) => (
              <Box key={data.id}>
                <Typography>{`${index + 1}. ${data.service.name}`}</Typography>
                {Boolean(data.service.document?.length) && (
                  <>
                    {data.service.document
                      ?.filter((item) => item.checked)
                      .map((subItem) => (
                        <Typography key={subItem.service.id} ml={2}>
                          {subItem.service.name}
                        </Typography>
                      ))}
                  </>
                )}
              </Box>
            ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.DIAGNOSIS:
      return hasDiagnosis(assessment.diagnosis) ? (
        <PrintRow label={assessmentLabels.diagnosis}>
          <DiagnosisRenderer data={assessment.diagnosis} />
        </PrintRow>
      ) : null;

    case SORT_ORDER.TABULAR:
      return assessment.tabularComponent && !!tabularComponentData ? (
        <EditableTableView tableState={tabularComponentData} />
      ) : null;

    case SORT_ORDER.TREATMENT_PLAN:
      return assessment.treatmentPlan?.length > 0 ? (
        <PrintRow label={assessmentLabels.treatmentPlan}>{assessment.treatmentPlan}</PrintRow>
      ) : null;

    case SORT_ORDER.TREATMENT_GIVEN:
      return assessment.treatmentGiven?.length > 0 ? (
        <PrintRow label={assessmentLabels.treatmentGiven}>{assessment.treatmentGiven}</PrintRow>
      ) : null;

    case SORT_ORDER.MEDICATION:
      return (assessment.medication?.length > 0 || assessment.productMedication?.length > 0) &&
        (Array.isArray(assessment.medication) || Array.isArray(assessment.productMedication)) ? (
        <PrintRow label={tl("assessment.medicationAndSupplements")}>
          {[...assessment.medication, ...productMedication].map((data, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={`med-${index}`}>
              <Typography>
                {`${index + 1}. ${data.genericName || ""}${data.genericName && data.brand && "/"} ${
                  data.brand
                }${" "}${data.strength}${" "} ${data.form ? `(${data.form})` : ""}
                  `}
              </Typography>

              <Box pl={2}>
                <Typography>{getPrintableMedicationInstruction(data)}</Typography>
              </Box>

              {Array.isArray(data?.partsOfDay) && data.partsOfDay.length > 0 && (
                <Typography>
                  <Box fontWeight={450} component="span" fontSize="0.8rem">
                    {`${t("assessment.medication.partsOfDay")} : ${data?.partsOfDay.join(", ")}`}
                  </Box>
                </Typography>
              )}

              {Array.isArray(data?.timesOfDay) && data.timesOfDay.length > 0 && (
                <Typography>
                  <Box fontWeight={450} component="span" fontSize="0.8rem">
                    {`${t("assessment.medication.timesOfDay")} : ${data.timesOfDay.join(", ")}`}
                  </Box>
                </Typography>
              )}

              <Box pl={0.5}>
                <Typography>{data.remark && <>Remarks: {data.remark}</>}</Typography>
              </Box>
            </Box>
          ))}
        </PrintRow>
      ) : null;

    case SORT_ORDER.FOLLOWUP:
      return assessment.followup ? (
        <PrintRow label={assessmentLabels.followup}>
          <>
            {assessment.followup?.days && (
              <Typography component="span">{`Follow up in ${
                assessment.followup.days
              } ${getFollowupFormat(assessment.followup)} ${assessment.followup.note}`}</Typography>
            )}

            {assessment.followup?.date && (
              <>
                <Typography>
                  {`Follow up on ${
                    convertADtoBS(moment(assessment.followup.date).toDate()).formatted2
                  } (${moment(assessment.followup.date).format("YYYY-MM-DD")} AD) ${
                    assessment.followup.startDate
                      ? ` from ${moment(assessment.followup.startDate).format(
                          "h:mm A"
                        )} to ${moment(assessment.followup.endDate).format("h:mm A")}`
                      : ""
                  }`}
                </Typography>

                {assessment.followup.note && (
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                    {assessment.followup.note}
                  </Typography>
                )}
              </>
            )}
          </>
        </PrintRow>
      ) : null;

    case SORT_ORDER.ADVICE:
      return assessment.advice ? (
        <PrintRow label={assessmentLabels.advice}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.advice}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.OTHERS:
      return assessment.others?.length > 0 ? (
        <PrintRow label={assessmentLabels.others}>{assessment.others}</PrintRow>
      ) : null;

    case SORT_ORDER.DERMASCOPY:
      return assessment.dermascopy ? (
        <PrintRow label={assessmentLabels.dermascopy || "Dermascopy"}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.dermascopy}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.OTHER1:
      return assessment.other1 ? (
        <PrintRow label={assessmentLabels.other1 || "Other1"}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.other1}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.OTHER2:
      return assessment.other2 ? (
        <PrintRow label={assessmentLabels.other2 || "Other2"}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.other2}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.OTHER3:
      return assessment.other3 ? (
        <PrintRow label={assessmentLabels.other3 || "Other3"}>
          <span style={{ whiteSpace: "pre-line" }}>{assessment.other3}</span>
        </PrintRow>
      ) : null;

    case SORT_ORDER.SKIN_ANALYSIS:
      return assessment?.skinAnalysis?.length > 0 ? (
        <PrintRow label={assessmentLabels.skinAnalysis || "Skin Analysis"}>
          <Box sx={{ width: "100%" }}>
            {assessment.skinAnalysis.map((skinData, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={index} display="flex" justifyContent="space-between" flexWrap="wrap">
                <Box display="flex" flexBasis="230px">
                  <Typography>{`${startCase(skinData?.name)} : ${
                    skinData?.reading || ""
                  }`}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </PrintRow>
      ) : null;

    default:
      return null;
  }
};
